import React, { useEffect, useState } from 'react';
import { Apple, X, ArrowRight, RotateCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getMusicKit, isAppleMusicAuthorized } from '../utils/musicKitLoader';

/**
 * Sticky top banner that appears whenever the host is signed into MusicBox
 * but has NOT yet connected an Apple Music subscription. Without that
 * connection, playback is impossible — so making this state obvious upfront
 * (rather than only after a failed Play click) saves the host a ton of
 * confused troubleshooting.
 *
 * The banner is dismissible per-session via localStorage. It re-appears on
 * the next browser tab/session — we don't want a host to permanently silence
 * a warning that gates the core functionality of the app.
 */
const DISMISS_KEY = 'spin.appleMusic.bannerDismissed';

export const AppleMusicBanner = () => {
  const [authorized, setAuthorized] = useState(true); // optimistic: hide until proven false
  const [ready, setReady] = useState(false);
  const [drmUnavailable, setDrmUnavailable] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    const onDrm = () => setDrmUnavailable(true);
    window.addEventListener('spin:appleMusic:drmUnavailable', onDrm);
    return () => window.removeEventListener('spin:appleMusic:drmUnavailable', onDrm);
  }, []);

  // Auto-clear the DRM banner if MusicKit successfully transitions back to
  // PLAYING — Widevine sometimes drops then recovers (network blip, device
  // power-save coming out of throttle). No point keeping the scary banner
  // up once audio is flowing again.
  useEffect(() => {
    if (!drmUnavailable) return undefined;
    let interval;
    (async () => {
      try {
        const m = await getMusicKit();
        interval = setInterval(() => {
          if (m.playbackState === 2) setDrmUnavailable(false);
        }, 1000);
      } catch { /* noop */ }
    })();
    return () => { if (interval) clearInterval(interval); };
  }, [drmUnavailable]);

  const handleRetry = async () => {
    try {
      const m = await getMusicKit();
      await m.play();
      setDrmUnavailable(false);
    } catch {
      /* If retry fails, the next prepare attempt will re-dispatch the event */
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await getMusicKit();
        if (cancelled) return;
        setAuthorized(!!m.isAuthorized);
        setReady(true);
        m.addEventListener('authorizationStatusDidChange', () => {
          setAuthorized(isAppleMusicAuthorized());
        });
      } catch {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!ready) return null;
  // DRM banner — show with a Retry CTA; auto-clears when playback resumes.
  if (drmUnavailable) {
    return (
      <div
        className="w-full bg-gradient-to-r from-[#FF3B3B]/20 via-[#FF3B3B]/15 to-transparent border-b border-[#FF3B3B]/40"
        data-testid="apple-music-drm-banner"
      >
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-start gap-3">
          <Apple className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-[12px] sm:text-[13px] text-white leading-snug">
            <span className="font-bold">Playback paused — Widevine DRM dropped.</span>{' '}
            <span className="text-white/80">
              Apple Music streams require Widevine; Android Chrome occasionally revokes the CDM session
              on power-save or network changes. Tap Retry — if it keeps failing, switch the DJ device
              to a Mac, iPhone, iPad, or desktop Chrome with Protected Content enabled.
            </span>
          </div>
          <button
            type="button"
            onClick={handleRetry}
            className="flex-shrink-0 inline-flex items-center gap-1 bg-white text-black text-[11px] font-bold uppercase tracking-[0.1em] rounded-full px-3 py-1.5 hover:bg-white/90 transition-colors"
            data-testid="drm-retry-button"
          >
            <RotateCw className="w-3 h-3" /> Retry
          </button>
        </div>
      </div>
    );
  }
  if (authorized || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
  };

  return (
    <div
      className="w-full bg-gradient-to-r from-[#FF007F]/15 via-[#FF007F]/10 to-transparent border-b border-[#FF007F]/30"
      data-testid="apple-music-banner"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <Apple className="w-4 h-4 text-white flex-shrink-0" />
        <p className="flex-1 text-[12px] sm:text-[13px] text-white leading-tight">
          <span className="font-bold">Apple Music not connected.</span>{' '}
          <span className="text-white/70">Playback is gated by an Apple Music subscription — connect it before going live.</span>
        </p>
        <Link
          to="/settings"
          className="bg-white text-black font-bold text-[11px] tracking-[0.1em] uppercase rounded-full px-3 py-1.5 flex items-center gap-1 hover:bg-white/90 transition-colors flex-shrink-0"
          data-testid="apple-music-banner-cta"
        >
          Connect <ArrowRight className="w-3 h-3" />
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-white/40 hover:text-white/80 transition-colors flex-shrink-0"
          aria-label="Dismiss"
          data-testid="apple-music-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AppleMusicBanner;
